import { useState } from "react";
import { Modal } from "../../components/ui/modal";
import { useToast } from "../../components/ui/toast";
import { ApiV2Error } from "../../lib/api-v2";
import { cn } from "../../lib/utils";
import { formatWhen } from "../orders/format-when";
import { credits, rupees } from "../products/types";
import { useRefundRequest, useRefundReview } from "./queries";
import { REFUND_STATUS_LABEL, RefundStatusBadge } from "./refund-status-badge";

const errorMessage = (err: unknown, fallback: string) => (err instanceof ApiV2Error ? err.message : fallback);

const RAZORPAY_STATUS: Record<string, string> = {
  pending: "Pending with Razorpay",
  processed: "Processed",
  failed: "Failed",
};

interface RefundReviewModalProps {
  requestId: string;
  onClose: () => void;
}

/**
 * One refund request: why the account asked, what approving would refund now (only
 * its credits still unused), and the way to approve, reject or re-check it.
 */
export function RefundReviewModal({ requestId, onClose }: RefundReviewModalProps) {
  const request = useRefundRequest(requestId);
  const review = useRefundReview();
  const toast = useToast();
  const [note, setNote] = useState("");
  const [rejectTouched, setRejectTouched] = useState(false);

  const r = request.data;
  const busy = review.approve.isPending || review.reject.isPending || review.refresh.isPending;
  const quote = r?.quote ?? null;

  const approve = () =>
    review.approve.mutate(
      { id: requestId, note: note.trim() || null },
      {
        onSuccess: (done) =>
          toast.success(
            done.status === "refunded" ? "Refunded" : "Refund started",
            `${rupees(done.total_amount ?? 0)}${done.refund_destination ? ` to ${done.refund_destination}` : ""} · ${credits(done.credits ?? 0)} credits taken back`,
          ),
        onError: (err) => toast.danger("Could not refund", errorMessage(err, "Try again.")),
      },
    );

  const reject = () => {
    setRejectTouched(true);
    if (!note.trim()) return;
    review.reject.mutate(
      { id: requestId, note: note.trim() },
      {
        onSuccess: () => toast.info("Refund request rejected", r?.reference_number ?? ""),
        onError: (err) => toast.danger("Could not reject", errorMessage(err, "Try again.")),
      },
    );
  };

  const recheck = () =>
    review.refresh.mutate(requestId, {
      onSuccess: (done) => toast.info("Refund re-checked", REFUND_STATUS_LABEL[done.status]),
      onError: (err) => toast.danger("Could not re-check", errorMessage(err, "Try again.")),
    });

  return (
    <Modal
      title="Refund request"
      subtitle={r ? `Order ${r.reference_number}` : undefined}
      onClose={onClose}
      width="max-w-lg"
      footer={
        r?.status === "requested" ? (
          <>
            <button
              onClick={reject}
              disabled={busy}
              className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft disabled:opacity-60"
            >
              {review.reject.isPending ? "Rejecting…" : "Reject"}
            </button>
            <button
              onClick={approve}
              disabled={busy || !quote || quote.credits < 1}
              className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:bg-line disabled:text-ink-3"
            >
              {review.approve.isPending ? "Refunding…" : `Approve & refund${quote && quote.credits > 0 ? ` ${rupees(quote.total)}` : ""}`}
            </button>
          </>
        ) : r?.status === "processing" ? (
          <button
            onClick={recheck}
            disabled={busy}
            className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft disabled:opacity-60"
          >
            {review.refresh.isPending ? "Checking…" : "Re-check with Razorpay"}
          </button>
        ) : (
          <button onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">
            Close
          </button>
        )
      }
    >
      {request.isLoading ? (
        <div className="h-40 animate-pulse rounded-lg bg-soft" />
      ) : request.isError || !r ? (
        <p className="text-sm text-danger">{errorMessage(request.error, "Could not load the request.")}</p>
      ) : (
        <div className="space-y-5 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-ink-3">
              {r.requested_by_email ?? r.client_id} · {formatWhen(r.created_at)}
            </span>
            <RefundStatusBadge status={r.status} />
          </div>

          <div>
            <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-ink-3">Reason</h3>
            <p className="text-ink">{r.reason}</p>
          </div>

          {r.status === "requested" && quote && (
            <dl className="space-y-1.5 rounded-xl border border-line p-4" aria-label="What approving refunds">
              <Row label="Credits left from this order" value={credits(quote.order_credits_left)} />
              <Row label="Unused paid credits on the account" value={credits(Math.floor(quote.unused_paid_credits))} />
              <Row label={`Refundable credits × ${rupees(quote.rate_per_minute ?? 0)}/min`} value={credits(quote.credits)} strong />
              <Row label="Amount" value={rupees(quote.amount)} />
              <Row label={`GST (${quote.gst_rate_percent}%)`} value={rupees(quote.gst_amount)} />
              <Row label="Refund" value={rupees(quote.total)} strong />
              {quote.credits < 1 && (
                <p className="pt-1 text-danger">No unused credits are left from this order; reject the request.</p>
              )}
            </dl>
          )}

          {r.total_amount !== null && r.credits !== null && (
            <dl className="space-y-1.5 rounded-xl border border-line p-4" aria-label="Razorpay refund">
              <Row
                label={r.status === "refunded" ? "Refunded" : r.status === "failed" ? "Refund attempted" : "Refunding"}
                value={rupees(r.total_amount)}
                strong
              />
              <Row label="Credits taken back" value={credits(r.credits)} />
              <Row label="Back to" value={r.refund_destination ?? "The account it was paid from"} />
              {r.ref_refund_id && <Row label="Razorpay refund" value={r.ref_refund_id} />}
              {r.ref_refund_status && (
                <Row label="Razorpay status" value={RAZORPAY_STATUS[r.ref_refund_status] ?? r.ref_refund_status} />
              )}
            </dl>
          )}
          {r.failure_reason && <p className="rounded-lg bg-danger-soft px-3 py-2 text-danger">{r.failure_reason}</p>}
          {r.review_note && r.status !== "requested" && (
            <p className="text-ink-2">
              Note from {r.reviewed_by_email ?? "admin"}: {r.review_note}
            </p>
          )}

          {r.status === "requested" && (
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-2">Note (required to reject)</span>
              <textarea
                value={note}
                rows={2}
                maxLength={1000}
                onChange={(e) => setNote(e.target.value)}
                aria-label="Review note"
                className={cn(
                  "w-full resize-y rounded-lg border px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/30",
                  rejectTouched && !note.trim() ? "border-danger/50" : "border-line focus:border-brand",
                )}
              />
              {rejectTouched && !note.trim() && (
                <span className="mt-1 block text-xs text-danger">Say why the refund is rejected.</span>
              )}
            </label>
          )}

          <div>
            <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-ink-3">History</h3>
            <ol className="space-y-1.5" aria-label="Request history">
              {r.history.map((h, i) => (
                <li key={i} className="flex justify-between gap-4">
                  <span className="text-ink">
                    {REFUND_STATUS_LABEL[h.status]}
                    {(h.note || h.actor_email) && <span className="text-ink-3"> · {h.note ?? h.actor_email}</span>}
                  </span>
                  <span className="whitespace-nowrap tabular-nums text-ink-3">{formatWhen(h.at)}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cn("flex justify-between gap-4", strong ? "font-semibold text-ink" : "text-ink-2")}>
      <dt>{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}
